import {
  eventFactory,
  type EventSpecificPayload,
  generatePayloadBase,
  generateUniqueId,
  sendPixelEvent,
} from '@polar-analytics/pixel-sdk';
import { captureException } from '@sentry/nextjs';
import { env } from 'lib/constants/env';

import { AnalyticsBase } from '../base';
import { AnalyticsID } from '../constants';
import type { PolarAnalyticsSink } from '../types';
import { getPageType } from './pageType';
import {
  cartLinePayloadFromItem,
  cartPayload,
  productVariantPayload,
} from './payloads';

const SHOP_URL = env.NEXT_PUBLIC_SHOPIFY_HOST;
const SITE_URL = env.NEXT_PUBLIC_SITE_URL;
const POLAR_ENDPOINT = env.NEXT_PUBLIC_POLAR_PIXEL_ENDPOINT;

type PolarCustomer = { email?: string };

const commonPayload = () => ({
  shopifyEventId: generateUniqueId(),
  shopifyShopURL: SHOP_URL,
});

export class PolarAnalytics
  extends AnalyticsBase
  implements PolarAnalyticsSink
{
  id = AnalyticsID.PolarSdk;

  private customer?: PolarCustomer;

  constructor() {
    super();
    this.isInitialized = !!POLAR_ENDPOINT;
  }

  setCustomer(customer?: PolarCustomer) {
    this.customer = customer;
  }

  private async send(data: NonNullable<EventSpecificPayload['data']>) {
    if (!POLAR_ENDPOINT) return;

    try {
      const payloadBase = await generatePayloadBase({
        customer: this.customer,
      });
      const result = await sendPixelEvent(POLAR_ENDPOINT, payloadBase, {
        data,
      });
      if (!result.success) captureException(result.error);
    } catch (err) {
      captureException(err);
    }
  }

  events = {
    pageView: ({ url }) =>
      this.send(
        eventFactory.page_viewed({
          ...commonPayload(),
          shopifyPageType: getPageType(url),
        }),
      ),

    viewProductPage: ({ product, pagePath }) => {
      const [variant] = product.variants;

      if (!variant) return;

      return this.send(
        eventFactory.product_viewed({
          ...commonPayload(),
          shopifyPageType: 'product',
          shopifyPageDetail: pagePath,
          shopifyPageProductId: product.id,
          shopifyEventData: {
            productVariant: productVariantPayload(
              product,
              variant,
              new URL(pagePath, SITE_URL).toString(),
            ),
          },
        }),
      );
    },

    viewCart: ({ cart }) =>
      this.send(
        eventFactory.cart_viewed({
          ...commonPayload(),
          shopifyEventData: { cart: cartPayload(cart) },
        }),
      ),

    addToCart: (items) =>
      Promise.all(
        items.map((item) => {
          const cartLine = cartLinePayloadFromItem(item);

          if (!cartLine) return undefined;

          return this.send(
            eventFactory.product_added_to_cart({
              ...commonPayload(),
              shopifyEventData: { cartLine },
            }),
          );
        }),
      ),
  } satisfies PolarAnalyticsSink['events'];
}
